import AdSenseBanner from "@/components/AdSenseBanner";
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchShopProduct, fileUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Coins, ArrowLeft, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const { add } = useShop();

  useEffect(() => {
    setP(null); setErr(false);
    fetchShopProduct(slug).then(setP).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="pt-32 pb-24 text-center text-white/60" data-testid="product-not-found">Product not found. <Link to="/store" className="text-[#FFBF00]">Back to store</Link></div>;
  if (!p) return <div className="pt-32 pb-24 px-6 max-w-5xl mx-auto"><div className="h-96 rounded-2xl glass skeleton" /></div>;

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-product">
      <div className="max-w-5xl mx-auto">
        <Link to="/store" className="inline-flex items-center gap-2 text-white/60 hover:text-[#FFBF00] mb-6"><ArrowLeft size={16} /> Back to store</Link>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl glass overflow-hidden h-80 md:h-[26rem] bg-black/40">
            {(p.images || [])[0] && <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#FF8C00] mb-2">{p.category}</div>
            <h1 className="font-display text-3xl mb-4">{p.name}</h1>

            {p.short_description && (
              <div
                className="
                  product-short-description
                  text-white/70
                  leading-7
                  mb-6
                  [&_p]:mb-3
                  [&_p:last-child]:mb-0
                  [&_strong]:text-white
                  [&_b]:text-white
                  [&_a]:text-[#FFBF00]
                  [&_a]:underline
                  [&_img]:max-w-full
                  [&_img]:rounded-xl
                "
                dangerouslySetInnerHTML={{ __html: p.short_description }}
              />
            )}

            <ul className="space-y-1.5 text-sm text-white/70 mb-6">
              {(p.perks || []).map((perk, i) => <li key={i}>✓ {perk}</li>)}
            </ul>
            <div className="flex items-center gap-5 mb-6">
              <div className="font-display text-3xl">₹{p.price_inr}</div>
              {p.dc_price != null && (
                <div className="inline-flex items-center gap-1.5 text-[#FFBF00] text-lg font-semibold">
                  <Coins size={18} /> {p.dc_price} DC
                </div>
              )}
            </div>
            {p.dc_price == null && <div className="text-xs text-red-400 mb-4">DC Coin payment is disabled for this product.</div>}
            <div className="flex gap-3">
              <button onClick={() => { add(p, 1); sfx.click(); toast.success("Added to cart"); }} data-testid="product-add-cart"
                className="px-6 py-3 rounded-full glass hover-blocky font-semibold">Add to cart</button>
              <button onClick={() => { add(p, 1); nav("/cart"); }} data-testid="product-buy-now"
                className="px-6 py-3 rounded-full bg-[#FFBF00] text-black font-display blocky-shadow-black hover-blocky inline-flex items-center gap-2">
                <ShoppingCart size={18} /> Buy now
              </button>
            </div>
          </div>
        </div>

        {p.description && (
          <section className="mt-10 rounded-2xl glass p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF8C00] mb-3">
              Product Details
            </div>

            <h2 className="font-display text-2xl sm:text-3xl mb-6">
              Full Description
            </h2>

            <div
              className="
                product-description
                text-white/80
                leading-7
                max-w-none

                [&_p]:mb-5
                [&_p:last-child]:mb-0

                [&_h1]:font-display
                [&_h1]:text-3xl
                [&_h1]:text-white
                [&_h1]:mt-8
                [&_h1]:mb-4

                [&_h2]:font-display
                [&_h2]:text-2xl
                [&_h2]:text-white
                [&_h2]:mt-8
                [&_h2]:mb-4

                [&_h3]:font-display
                [&_h3]:text-xl
                [&_h3]:text-white
                [&_h3]:mt-6
                [&_h3]:mb-3

                [&_strong]:text-white
                [&_b]:text-white

                [&_a]:text-[#FFBF00]
                [&_a]:underline

                [&_ul]:list-disc
                [&_ul]:pl-6
                [&_ul]:mb-5

                [&_ol]:list-decimal
                [&_ol]:pl-6
                [&_ol]:mb-5

                [&_li]:mb-2

                [&_img]:block
                [&_img]:max-w-full
                [&_img]:h-auto
                [&_img]:rounded-xl
                [&_img]:mx-auto
                [&_img]:my-6

                [&_figure]:my-6
                [&_figure]:text-center

                [&_figcaption]:text-sm
                [&_figcaption]:text-white/50
                [&_figcaption]:mt-2

                [&_table]:w-full
                [&_table]:border-collapse
                [&_td]:border
                [&_td]:border-white/10
                [&_td]:p-3
                [&_th]:border
                [&_th]:border-white/10
                [&_th]:p-3
                [&_th]:text-white
              "
              dangerouslySetInnerHTML={{ __html: p.description }}
            />
          </section>
        )}

        {/* Bottom AdSense Banner */}
        <div className="mt-12">
          <AdSenseBanner />
        </div>
      </div>
    </div>
  );
}